import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";

import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import EdsLogo from "./EdsLogo";
import EdsIcon from "./EdsIcon";
import { EDS, edsCssVars, btnPrimary, resetButton } from "../theme";

function toKey(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

export default function StorefrontLayout({ children, maxWidth, hideHeader }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart();
  const { token, pluginRole } = useAuth();
  const { tenant } = useStorefrontTenant();

  const role = toKey(pluginRole);
  const portalUser = isPortalRole(role);
  const distributor = isDistributorRole(role);
  const isAuthed = !!token;
  const brandName = String(tenant?.name || "Elite Drive Solutions").trim();

  const isMobileRoute =
    location.pathname === "/m" || location.pathname.startsWith("/m/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const qFromUrl = useMemo(() => {
    try {
      const p = new URLSearchParams(location.search || "");
      return String(p.get("q") || "");
    } catch {
      return "";
    }
  }, [location.search]);

  const [q, setQ] = useState(qFromUrl);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setQ(qFromUrl);
  }, [qFromUrl]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const submitSearch = (value) => {
    const next = String(value || "").trim();
    const params = next ? createSearchParams({ q: next }).toString() : "";
    navigate(`${toPath("/catalog")}${params ? `?${params}` : ""}`);
  };

  const onChangeQ = (value) => {
    const next = String(value || "");
    setQ(next);
    if (location.pathname === toPath("/catalog")) {
      const trimmed = next.trim();
      const params = new URLSearchParams(location.search || "");
      if (trimmed) params.set("q", trimmed);
      else params.delete("q");
      navigate(
        `${toPath("/catalog")}${
          params.toString() ? `?${params.toString()}` : ""
        }`,
        { replace: true },
      );
    }
  };

  const goToSessionDestination = () => {
    if (!isAuthed) {
      navigate(toPath("/authentication/sign-in"));
      return;
    }
    if (portalUser) {
      navigate(distributor ? "/account/valve-bodies" : "/account");
      return;
    }
    navigate("/post-login");
  };

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Catalog", path: "/catalog" },
    ...(isAuthed ? [] : [{ label: "Support", path: "/register" }]),
  ];

  const pageBody =
    maxWidth === null ? (
      children
    ) : (
      <Container maxWidth={maxWidth || "xl"} sx={{ py: { xs: 3, md: 5 } }}>
        {children}
      </Container>
    );

  return (
    <Box
      sx={{
        ...edsCssVars,
        minHeight: "100vh",
        bgcolor: EDS.canvas,
        color: EDS.ink,
        fontFamily: EDS.fontDisplay,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {!hideHeader ? (
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1200,
            bgcolor: scrolled ? "rgba(255,255,255,0.92)" : EDS.surface,
            backdropFilter: scrolled ? "blur(16px)" : "none",
            WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
            transition: "box-shadow 200ms ease",
            boxShadow: scrolled ? EDS.shadowSoft : "none",
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                minHeight: EDS.headerHeight,
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, md: 4 },
              }}
            >
              <Box
                onClick={() => navigate(toPath("/"))}
                sx={{ cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }}
              >
                <EdsLogo variant="header" />
              </Box>

              {!isMobileRoute ? (
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {navLinks.map((link) => {
                    const active =
                      link.path === "/"
                        ? location.pathname === toPath("/")
                        : location.pathname.startsWith(toPath(link.path));
                    return (
                      <Box
                        key={link.path}
                        component="button"
                        type="button"
                        onClick={() => navigate(toPath(link.path))}
                        sx={{
                          ...resetButton,
                          color: active ? EDS.ink : EDS.muted,
                          fontWeight: active ? 500 : 400,
                          fontSize: "0.875rem",
                          letterSpacing: "-0.01em",
                          py: 0.5,
                          transition: "color 160ms ease",
                          "&:hover": { color: EDS.ink },
                        }}
                      >
                        {link.label}
                      </Box>
                    );
                  })}
                </Stack>
              ) : null}

              <Box
                sx={{
                  flex: 1,
                  maxWidth: 320,
                  mx: "auto",
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  gap: 0.75,
                  color: EDS.subtle,
                  borderBottom: `1px solid ${EDS.line}`,
                  pb: 0.6,
                }}
              >
                <EdsIcon name="search" size={18} color={EDS.subtle} />
                <InputBase
                  value={q}
                  placeholder="Search"
                  onChange={(e) => onChangeQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitSearch(q);
                    }
                  }}
                  sx={{
                    flex: 1,
                    color: EDS.ink,
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    "& input": { p: 0 },
                    "& input::placeholder": {
                      color: EDS.subtle,
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: "auto" }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate(toPath("/cart"))}
                  aria-label="Cart"
                  sx={{
                    ...resetButton,
                    color: EDS.ink,
                    display: "grid",
                    placeItems: "center",
                    p: 0.25,
                  }}
                >
                  <Badge
                    badgeContent={itemCount || 0}
                    invisible={!itemCount}
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: EDS.ink,
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: "0.625rem",
                        minWidth: 15,
                        height: 15,
                        boxShadow: "none",
                      },
                    }}
                  >
                    <EdsIcon name="cart" size={22} color={EDS.ink} />
                  </Badge>
                </Box>

                <Box
                  component="button"
                  type="button"
                  onClick={goToSessionDestination}
                  sx={{
                    ...btnPrimary,
                    display: { xs: "none", sm: "block" },
                    py: 0.75,
                    px: 1.75,
                    fontSize: "0.8125rem",
                  }}
                >
                  {isAuthed ? "Account" : "Sign in"}
                </Box>

                <Box
                  component="button"
                  type="button"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  aria-label="Menu"
                  sx={{
                    ...resetButton,
                    color: EDS.ink,
                    display: { xs: "grid", md: "none" },
                    placeItems: "center",
                    p: 0.25,
                  }}
                >
                  <EdsIcon name="menu" size={22} color={EDS.ink} />
                </Box>
              </Stack>
            </Box>
          </Container>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 168,
                borderRadius: "10px",
                boxShadow: "0 8px 32px rgba(15,23,42,0.1)",
              },
            }}
          >
            {navLinks.map((link) => (
              <MenuItem
                key={link.path}
                onClick={() => {
                  setAnchorEl(null);
                  navigate(toPath(link.path));
                }}
                sx={{ fontSize: "0.875rem", fontWeight: 400, color: EDS.ink }}
              >
                {link.label}
              </MenuItem>
            ))}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                goToSessionDestination();
              }}
              sx={{ fontSize: "0.875rem", fontWeight: 400, color: EDS.ink }}
            >
              {isAuthed ? "My account" : "Sign in"}
            </MenuItem>
          </Menu>
        </Box>
      ) : null}

      <Box component="main">{pageBody}</Box>

      <Box component="footer" sx={{ mt: 12, pt: 6, pb: 4 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr" },
              gap: { xs: 4, md: 6 },
              pb: 5,
            }}
          >
            <Box>
              <EdsLogo variant="footer" />
              <Box
                component="p"
                sx={{
                  mt: 2,
                  mb: 0,
                  fontSize: "0.875rem",
                  lineHeight: 1.75,
                  color: EDS.muted,
                  maxWidth: 300,
                  fontWeight: 400,
                }}
              >
                Precision drivetrain solutions for shops and fleets.
              </Box>
            </Box>
            <Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  mb: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: EDS.subtle,
                }}
              >
                Shop
              </Box>
              {(isAuthed
                ? [
                    ["Catalog", "/catalog"],
                    ["Cart", "/cart"],
                  ]
                : [
                    ["Catalog", "/catalog"],
                    ["Cart", "/cart"],
                    ["Register", "/register"],
                  ]
              ).map(([label, path]) => (
                  <Box
                    key={label}
                    component="button"
                    type="button"
                    onClick={() => navigate(toPath(path))}
                    sx={{
                      ...resetButton,
                      display: "block",
                      color: EDS.muted,
                      mb: 0.5,
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      "&:hover": { color: EDS.ink },
                    }}
                  >
                    {label}
                  </Box>
              ))}
            </Box>
            <Box>
              <Box
                component="p"
                sx={{
                  m: 0,
                  mb: 1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: EDS.subtle,
                }}
              >
                Legal
              </Box>
              {[
                ["Privacy", "/privacy"],
                ["Delete account", "/delete-account"],
              ].map(([label, path]) => (
                <Box
                  key={path}
                  component="button"
                  type="button"
                  onClick={() => navigate(toPath(path))}
                  sx={{
                    ...resetButton,
                    display: "block",
                    color: EDS.muted,
                    mb: 0.5,
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    "&:hover": { color: EDS.ink },
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              pt: 3,
              borderTop: `1px solid ${EDS.line}`,
              fontSize: "0.75rem",
              color: EDS.subtle,
              fontWeight: 400,
            }}
          >
            © {new Date().getFullYear()} {brandName}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

StorefrontLayout.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  hideHeader: PropTypes.bool,
};

StorefrontLayout.defaultProps = {
  maxWidth: "xl",
  hideHeader: false,
};
